import React, {useEffect, useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link} from 'react-router-dom'
import {useFormik} from 'formik'
import {requestResetPassword} from '../core/_requests'
import {useSearchParams} from "react-router-dom";
import {useNavigate} from 'react-router-dom';

const resetPasswordSchema = Yup.object().shape({
  password: Yup.string()
    .min(8, 'Minimum 8 caractères.')
    .required('Mot de passe requis.'),
  passwordConfirmation: Yup.string()
    .required('Mot de passe requis.')
    .oneOf([Yup.ref('password')], 'Les deux mots de passe doivent correspondre.')
})

export function ResetPassword() {
  const [loading, setLoading] = useState(false)
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)
  const [show, setShow] = useState<boolean>(false)
  const [showConfirm, setShowConfirm] = useState<boolean>(false)
  const [token, setToken] = useState<string | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate();

  useEffect(() => {
    if (searchParams.has('token')) {
      setToken(searchParams.get('token'))
      searchParams.delete('token')
      setSearchParams(searchParams, {replace: true})
    }
  }, [searchParams, setSearchParams]);

  const formik = useFormik({
    initialValues: {password: "", passwordConfirmation: ""},
    validationSchema: resetPasswordSchema,
    onSubmit: (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      setHasErrors(undefined)
      setTimeout(() => {
        requestResetPassword(values.password, token)
          .then(response => {
            if (response.data) {
              setHasErrors(false)
              setTimeout(() => {
                navigate(`/auth/login`)
              }, 2000);
            } else {
              setHasErrors(true)
            }
            setLoading(false)
          })
          .catch(() => {
            setHasErrors(true)
            setLoading(false)
            setSubmitting(false)
            setStatus('The login detail is incorrect')
          })
      }, 1000)
    },
  })

  return (
    <div className='card custom-card p-8'>
      <form
        className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
        noValidate
        id='kt_login_password_reset_form'
        onSubmit={formik.handleSubmit}
      >
        <div className='text-center mb-10'>
          {/* begin::Title */}
          <h1 className='fw-bolder mb-3 gugi-font'>Réinitialiser le mot de passe</h1>
          {/* end::Title */}

          {/* begin::Link */}
          <div className='text-gray-500 fw-semibold fs-6'>
            Veuillez entrer le nouveau mot de passe.
          </div>
          {/* end::Link */}
        </div>

        {/* begin::Title */}
        {hasErrors === true && (
          <div className='mb-lg-10 alert alert-danger'>
            <div className='alert-text font-weight-bold'>
              Le token n'est plus valide ou a dépassé les 24h de validité, veuillez regenerer à nouveau
            </div>
          </div>
        )}

        {hasErrors === false && (
          <div className='mb-lg-10 alert alert-primary'>
            <div className='alert-text font-weight-bold'>
              Le mot de passe a été modifié avec succes
            </div>
          </div>
        )}
        {/* end::Title */}

        {/* begin::Form group */}
        <div className='fv-row mb-3'>
          <label className='form-label fw-bolder text-dark fs-6 mb-0'>Mot de passe</label>
          <div className='position-relative'>
            <input
              type={!show ? 'password' : 'text'}
              autoComplete='off'
              {...formik.getFieldProps('password')}
              className={clsx(
                'form-control',
                {
                  'is-invalid': formik.errors.password,
                }
              )}
            />
            <i
              className={`ki-outline ${
                !show ? 'ki-eye-slash' : 'ki-eye'
              }  position-absolute fs-2 cursor-pointer`}
              style={{
                top: '50%',
                right: formik.errors.password ? '40px' : '15px',
                transform: 'translateY(-50%)',
                transition: 'right ease 0.3s'
              }}
              onClick={() => setShow(!show)}
            ></i>
          </div>
          {formik.errors.password && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.password}</span>
              </div>
            </div>
          )}
        </div>
        {/* end::Form group */}

        {/* begin::Form group */}
        <div className='fv-row mb-8'>
          <label className='form-label fw-bolder text-dark fs-6 mb-0'>Confirmer le mot de passe</label>
          <div className='position-relative'>
            <input
              type={!showConfirm ? 'password' : 'text'}
              autoComplete='off'
              {...formik.getFieldProps('passwordConfirmation')}
              className={clsx(
                'form-control',
                {
                  'is-invalid': formik.errors.passwordConfirmation,
                },
              )}
            />
            <i
              className={`ki-outline ${
                !showConfirm ? 'ki-eye-slash' : 'ki-eye'
              }  position-absolute fs-2 cursor-pointer`}
              style={{
                top: '50%',
                right: formik.errors.passwordConfirmation ? '40px' : '15px',
                transform: 'translateY(-50%)',
                transition: 'right ease 0.3s'
              }}
              onClick={() => setShowConfirm(!showConfirm)}
            ></i>
          </div>
          {formik.errors.passwordConfirmation && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.passwordConfirmation}</span>
              </div>
            </div>
          )}
        </div>
        {/* end::Form group */}

        {/* begin::Form group */}
        <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
          <button type='submit' className='btn btn-primary me-4' id='kt_password_reset_submit' disabled={loading}>
            {!loading && 'Valider'}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                    Veuillez patienter...{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
            )}
          </button>

          <Link to='/auth/login'>
            <button
              type='button'
              id='kt_login_password_reset_form_cancel_button'
              className='btn btn-light'
              disabled={formik.isSubmitting || !formik.isValid}
            >
              Annuler
            </button>
          </Link>{' '}
        </div>
        {/* end::Form group */}
      </form>
    </div>
  )
}
